import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useHanziStore } from '../hooks/useHanziConfig'
import { TRACE_COLORS, FONT_OPTIONS } from '../utils/constants'
import { QUICK_INPUT_CATEGORIES } from '../data/quick-input'

import { getPageUsage } from '../utils/renderer'

const ConfigPanel: React.FC = () => {
  const { t } = useTranslation()
  const { config, history, updateConfig, setWordMode, setCharMode, deleteHistory } = useHanziStore()
  const [activeCategory, setActiveCategory] = useState<string | null>(null)
  const [draftChars, setDraftChars] = useState(config.chars)
  const [draftWords, setDraftWords] = useState<string[]>(config.words || [])

  const isWordMode = config.inputMode === 'word'
  const configWordsStr = (config.words || []).join(',')
  const draftWordsStr = draftWords.join(',')
  const hasPendingChanges = draftChars !== config.chars || draftWordsStr !== configWordsStr
  const pageUsage = getPageUsage(config)
  const hasContent = config.chars.length > 0

  useEffect(() => {
    setDraftChars(config.chars)
    setDraftWords(config.words || [])
  }, [config.chars, config.words])

  const handleCharsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDraftChars(e.target.value.slice(0, 180))
  }

  const appendChars = (chars: string): string => {
    const current = draftChars.replace(/[^\u4e00-\u9fa5]/g, '')
    const added = chars.replace(/[^\u4e00-\u9fa5]/g, '')
    return (current + added).slice(0, 180)
  }

  const handleConfirm = () => {
    const filtered = draftChars.replace(/[^\u4e00-\u9fa5]/g, '').slice(0, 180)
    if (draftWords.length > 0) {
      setWordMode(filtered, draftWords)
    } else {
      setCharMode(filtered)
    }
    setDraftChars(filtered)
  }

  const handleQuickInput = (chars: string, type: 'char' | 'word', words?: string[]) => {
    if (type === 'char' && isWordMode && hasContent) return
    if (type === 'word' && !isWordMode && hasContent) return
    const appended = appendChars(chars)
    if (type === 'word' && words && words.length > 0) {
      const newWords = [...draftWords, ...words].filter((w, i, arr) => arr.indexOf(w) === i).slice(0, 60)
      setDraftChars(appended)
      setDraftWords(newWords)
      setWordMode(appended, newWords)
    } else {
      setDraftChars(appended)
      setDraftWords([])
      setCharMode(appended)
    }
  }

  return (
    <div className="space-y-6">
      <section className="bg-white rounded-2xl shadow-sm border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-text">{t('input.label')}</h2>
          <div className="flex items-center gap-2">
            {isWordMode && (
              <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-medium">
                {t('input.wordMode')}
              </span>
            )}
            {config.chars.length > 0 && (
              <button
                onClick={() => {
                  setDraftChars('')
                  setCharMode('')
                }}
                className="text-xs text-text-light hover:text-primary transition-colors"
                title={t('input.clear')}
              >
                {t('input.clear')}
              </button>
            )}
          </div>
        </div>
        <textarea
          className="w-full px-4 py-3 rounded-xl border border-border bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all duration-200 h-32 resize-none font-mono"
          placeholder={t('input.placeholder')}
          value={draftChars}
          onChange={handleCharsChange}
        />
        <div className="flex items-center justify-between mt-2">
          <p className="text-xs text-text-light">
            {hasPendingChanges ? t('input.pendingHint') : t('input.filterHint')}
          </p>
          <p className="text-xs text-text-secondary">
            {t('input.charCount', { count: draftChars.length, max: 180 })}
          </p>
        </div>

        <button
          onClick={handleConfirm}
          disabled={!hasPendingChanges}
          className={`mt-3 w-full py-2.5 rounded-xl font-medium transition-all duration-200 ${
            hasPendingChanges
              ? 'bg-primary text-white hover:bg-primary-dark hover:shadow-md active:scale-95'
              : 'bg-border text-text-light cursor-not-allowed'
          }`}
        >
          {t('input.confirm')}
        </button>

        {hasContent && pageUsage.pageCount > 0 && (
          <div className="mt-3 flex items-center justify-between text-xs text-text-secondary">
            <span>{t('input.pageEstimate', { pages: pageUsage.pageCount })}</span>
            {!pageUsage.isFullPage && pageUsage.remainingRows > 0 && (
              <span>{t('input.remainingRows', { rows: pageUsage.remainingRows })}</span>
            )}
          </div>
        )}
      </section>

      <section className="bg-white rounded-2xl shadow-sm border border-border p-6">
        <h2 className="text-lg font-semibold text-text mb-4">{t('templates.title')}</h2>
        <div className="space-y-4">
          <div>
            <button
              disabled={isWordMode && hasContent}
              onClick={() => {
                if (isWordMode && hasContent) return
                setActiveCategory(activeCategory === 'char' ? null : 'char')
              }}
              className={`w-full flex items-center justify-between px-3 py-2 text-sm border border-border rounded-lg transition-colors text-text-secondary ${
                isWordMode && hasContent
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:border-primary hover:text-primary'
              }`}
            >
              <span>{t('templates.characters')}</span>
              <svg
                className={`w-4 h-4 transition-transform ${activeCategory === 'char' ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {activeCategory === 'char' && (
              <div className="mt-2 space-y-2 pl-2">
                {QUICK_INPUT_CATEGORIES.flatMap((cat) =>
                  cat.groups.filter((g) => g.type === 'char')
                ).map((group) => (
                  <button
                    key={group.id}
                    disabled={isWordMode && hasContent}
                    onClick={() => handleQuickInput(group.chars, group.type, group.words)}
                    className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors ${
                      isWordMode && hasContent
                        ? 'opacity-50 cursor-not-allowed'
                        : 'hover:bg-primary/5'
                    }`}
                  >
                    <span className="font-medium text-text">{group.title}</span>
                    <div className="text-xs text-text-light mt-1">{group.description}</div>
                    <div className="text-xs text-text-secondary mt-1 truncate">{group.chars.slice(0, 30)}...</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <button
              disabled={!isWordMode && hasContent}
              onClick={() => {
                if (!isWordMode && hasContent) return
                setActiveCategory(activeCategory === 'word' ? null : 'word')
              }}
              className={`w-full flex items-center justify-between px-3 py-2 text-sm border border-border rounded-lg transition-colors text-text-secondary ${
                !isWordMode && hasContent
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:border-primary hover:text-primary'
              }`}
            >
              <span>{t('templates.words')}</span>
              <svg
                className={`w-4 h-4 transition-transform ${activeCategory === 'word' ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {activeCategory === 'word' && (
              <div className="mt-2 space-y-2 pl-2">
                {QUICK_INPUT_CATEGORIES.flatMap((cat) =>
                  cat.groups.filter((g) => g.type === 'word')
                ).map((group) => (
                  <button
                    key={group.id}
                    disabled={!isWordMode && hasContent}
                    onClick={() => handleQuickInput(group.chars, group.type, group.words)}
                    className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors ${
                      !isWordMode && hasContent
                        ? 'opacity-50 cursor-not-allowed'
                        : 'hover:bg-primary/5'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-text">{group.title}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary">
                        {t('input.wordTag')}
                      </span>
                    </div>
                    <div className="text-xs text-text-light mt-1">{group.description}</div>
                    <div className="text-xs text-text-secondary mt-1 truncate">{group.chars.slice(0, 30)}...</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="bg-white rounded-2xl shadow-sm border border-border p-6">
        <h2 className="text-lg font-semibold text-text mb-4">{t('layout.title')}</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">{t('layout.gridType')}</label>
            <select
              value={config.gridType}
              onChange={(e) => updateConfig({ gridType: e.target.value as 'tian' | 'mi' | 'fang' })}
              className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
            >
              {(['tian', 'mi', 'fang'] as const).map((type) => (
                <option key={type} value={type}>
                  {t(`layout.${type}`)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">{t('layout.style')}</label>
            <select
              value={isWordMode ? 'default' : config.showPinyin ? 'pinyin' : config.showStrokeOrder ? 'strokeOrder' : 'default'}
              disabled={isWordMode}
              onChange={(e) => {
                const value = e.target.value
                if (value === 'pinyin') {
                  updateConfig({ showPinyin: true, showStrokeOrder: false })
                } else if (value === 'strokeOrder') {
                  updateConfig({ showPinyin: false, showStrokeOrder: true })
                } else {
                  updateConfig({ showPinyin: false, showStrokeOrder: false })
                }
              }}
              className={`w-full px-3 py-2 text-sm border border-border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all ${isWordMode ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <option value="default">{t('layout.styleDefault')}</option>
              <option value="pinyin">{t('layout.stylePinyin')}</option>
              <option value="strokeOrder">{t('layout.styleStrokeOrder')}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">{t('layout.traceCount')}</label>
            <input
              type="range"
              min={0}
              max={11}
              value={config.traceCount}
              onChange={(e) => updateConfig({ traceCount: Number(e.target.value) })}
              className="w-full"
            />
            <div className="text-center text-sm text-text-secondary mt-1">
              {config.traceCount}
            </div>
          </div>

          <div className="flex items-center gap-4 flex-wrap">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={config.insertBlankRow}
                onChange={(e) => updateConfig({ insertBlankRow: e.target.checked })}
                className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
              />
              <span className="text-sm text-text-secondary">{t('layout.insertBlankRow')}</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={config.insertBlankCol}
                onChange={(e) => updateConfig({ insertBlankCol: e.target.checked })}
                className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
              />
              <span className="text-sm text-text-secondary">{t('layout.insertBlankCol')}</span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">{t('layout.paperSize')}</label>
            <div className="grid grid-cols-2 gap-2">
              {(['a4', 'letter'] as const).map((size) => (
                <button
                  key={size}
                  onClick={() => updateConfig({ paperSize: size })}
                  className={`px-3 py-2 text-sm border rounded-lg transition-colors ${
                    config.paperSize === size
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-border text-text-secondary hover:border-primary hover:text-primary'
                  }`}
                >
                  {t(`layout.${size}`)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className={`block text-sm font-medium text-text-secondary mb-2 ${config.showStrokeOrder ? 'opacity-50' : ''}`}>{t('layout.font')}</label>
            <div className="grid grid-cols-3 gap-2">
              {(Object.keys(FONT_OPTIONS) as Array<keyof typeof FONT_OPTIONS>).map((key) => (
                <button
                  key={key}
                  disabled={config.showStrokeOrder}
                  onClick={() => updateConfig({ fontFamily: key })}
                  className={`px-3 py-2 text-sm border rounded-lg transition-colors ${
                    config.showStrokeOrder
                      ? 'opacity-50 cursor-not-allowed border-border text-text-light'
                      : config.fontFamily === key
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-border text-text-secondary hover:border-primary hover:text-primary'
                  }`}
                >
                  {FONT_OPTIONS[key].name}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">{t('layout.traceColor')}</label>
            <div className="flex gap-2 flex-wrap">
              {TRACE_COLORS.map((color) => (
                <button
                  key={color}
                  onClick={() => updateConfig({ traceColor: color })}
                  className={`w-8 h-8 rounded-full border-2 transition-all ${
                    config.traceColor === color ? 'border-text scale-110' : 'border-transparent'
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white rounded-2xl shadow-sm border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-text">{t('nav.history')}</h2>
          {history.length > 0 && (
              <button
                onClick={() => {
                  if (confirm(t('history.clearConfirm'))) {
                    history.forEach((h) => deleteHistory(h.id))
                  }
                }}
                className="text-xs text-text-light hover:text-primary transition-colors"
              >
                {t('input.clear')}
              </button>
          )}
        </div>
        {history.length === 0 ? (
          <p className="text-sm text-text-light">{t('history.empty')}</p>
        ) : (
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {history.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-primary/5 cursor-pointer group"
                onClick={() => {
                  setCharMode(item.chars.replace('...', ''))
                }}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-text font-medium truncate">{item.preview}</div>
                  <div className="text-xs text-text-light">
                    {new Date(item.timestamp).toLocaleDateString()} · {item.paperSize.toUpperCase()}
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    deleteHistory(item.id)
                  }}
                  className="ml-2 text-text-light hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="bg-white rounded-2xl shadow-sm border border-border p-6">
        <h2 className="text-lg font-semibold text-text mb-4">{t('actions.share')}</h2>
        <button className="bg-primary text-white px-6 py-3 rounded-xl font-semibold hover:bg-primary-dark transition-colors duration-200 w-full">{t('actions.shareLink')}</button>
      </section>
    </div>
  )
}

export default ConfigPanel
